import ReferenceIcon from '@assets/icons/note.svg?react'
import RecipientIcon from '@assets/icons/user.svg?react'
import { CoreInput, CorePaymentInput, CoreSelect, MMButton, SendMoneyMethodInfo, SuccessPane } from '@components'
import { useHeaderContext } from '@context/HeaderContext'
import { extendedSelectOptions, toastSuccess } from '@utils'
import { SkrillRecipientEdit } from '@views/recipients/elements/SkrillRecipientEdit'
import AccountingCategorySelect from '@withdrawals/components/AccountingCategorySelect'
import NoSuitableAccountModal from '@withdrawals/components/NoSuitableAccountModal'
import styles from '@withdrawals/styles/WithdrawInitiate.module.scss'
import WalletTransferPreview from '@withdrawals/views/WalletTransferPreview'
import {
  MerchantAccountTypes,
  PaymentOptionStatuses,
  paths,
  responseDoesNotContainsChallenge,
  selectMerchantAccountType,
  useAppSelector,
  useReturnToOrigin,
  useWalletTransferQry,
  WithdrawalSubFlows
} from 'mmfintech-backend-api'
import { GlobalContext, isValidObject, OtpContext, tr, useSearchParams } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

const SkrillTransfer = () => {
  const { modalShow, modalHide } = useContext(GlobalContext)
  const { setOtpOnSuccess } = useContext(OtpContext)

  const { currentStep, setCurrentStep } = useHeaderContext()

  const history = useHistory()

  const [result, setResult] = useState(null)
  const [promoCode, setPromoCode] = useState('') // Stored for final submission
  const { getInt } = useSearchParams()

  const recipientId = getInt('recipientId')
  const accountType = useAppSelector(selectMerchantAccountType)

  const { returnToOrigin } = useReturnToOrigin()
  const returnToOriginUrl = returnToOrigin({ recipientId })

  const {
    formValues,
    handleRecipientChange,
    handlePreview,
    handleInitiate,
    selectedAccount,
    selectedRecipient,
    setSelectedAccount,
    filteredRecipients,
    accounts,
    accountsError,
    accountsFetching,
    recipientsFetching,
    previewFetching,
    previewError,
    resetMutations,
    reloadRecipients
  } = useWalletTransferQry({
    flowType: WithdrawalSubFlows.SKRILL,
    editRecipient: (id?: number) => handleEditRecipient(id),
    onPreviewSuccess: () => setCurrentStep(2),
    onInitiateSuccess: (response: any) => handleInitSuccess(response),
    selectAddNew: false
  })

  const handleEditRecipient = (recipient?: any) => {
    const { currencyCode } = selectedAccount || {}
    if (currencyCode) {
      modalShow({
        header: recipient?.paymentInstrumentId
          ? tr('FRONTEND.RECIPIENTS.EDIT.TITLE', 'Recipient details')
          : tr('FRONTEND.RECIPIENTS.CREATE.TITLE', 'Create recipient'),
        content: (
          <SkrillRecipientEdit
            recipientId={recipient?.paymentInstrumentId}
            currencyCode={currencyCode}
            onClose={modalHide}
            onSuccess={(response: any) => {
              const { paymentInstrumentId } = response || {}
              toastSuccess(
                recipient?.paymentInstrumentId
                  ? tr('FRONTEND.RECIPIENTS.EDIT.SUCCESS', 'Recipient updated.')
                  : tr('FRONTEND.RECIPIENTS.CREATE.SUCCESS', 'Recipient successfully created.'),
                undefined,
                {
                  duration: 3_000
                }
              )
              reloadRecipients(paymentInstrumentId)
            }}
          />
        )
      })
    } else {
      modalShow({
        header: tr('FRONTEND.RECIPIENTS.CREATE.TITLE', 'Create recipient'),
        content: <NoSuitableAccountModal />,
        options: { size: 'medium', closeOnClickOutside: false }
      })
    }
  }

  const transferSuccess = (data: any) => {
    setResult(data)
    setCurrentStep(3)
  }

  const handleInitSuccess = (data: any) => {
    if (responseDoesNotContainsChallenge(data)) {
      transferSuccess(data)
    }
  }

  const handleAdvancedInitiate = () => {
    setOtpOnSuccess(() => handleInitSuccess)
    handleInitiate(promoCode)
  }

  useEffect(() => {
    setCurrentStep(1)

    return () => {
      setCurrentStep(1)
      setResult(null)
      resetMutations()
    }
    // eslint-disable-next-line
  }, [])

  return (
    <>
      {currentStep === 1 && (
        <div className={styles['send-money-container']} data-test='skrill-transfer-container'>
          <div className='from-container'>
            <div className='from-to-label-container'>
              <div className='from-to-label'>{tr('FRONTEND.WITHDRAW.SKRILL.FROM', 'From')}</div>
            </div>
            <CorePaymentInput
              selectedOption={selectedAccount}
              setSelectedAccount={setSelectedAccount}
              options={accounts}
              dataTest='deposit-payment-input-test'
              paymentOption={selectedAccount?.paymentOptions?.[0]}
              enableMaxAmountButton
              onChange={(value: any) => formValues.setValue('amount', value)}
              value={formValues.getValue('amount')}
              error={formValues.getError('amount')}
              isLoading={accountsFetching}
              noOptionsMessage={tr(
                'FRONTEND.WITHDRAW.SKRILL.NO_ACCOUNTS_WITH_BALANCE',
                'No accounts with enough balance'
              )}
              noOptionsAction={{
                label: tr('FRONTEND.WITHDRAW.NO_ACCOUNT.BUTTON_DEPOSIT', 'Deposit'),
                onClick: () => history.push(paths.banking.deposit())
              }}
            />
          </div>

          <div className='to-container'>
            <div className='from-to-label-container'>
              <div className='from-to-label'>{tr('FRONTEND.WITHDRAW.SKRILL.TO', 'To')}</div>

              {!recipientId && accountType !== MerchantAccountTypes.PROSPECT && (
                <MMButton
                  type='button'
                  size='normal'
                  variant='tertiary'
                  leftIcon={RecipientIcon}
                  caption={tr('FRONTEND.WITHDRAW.DIGITAL_WALLET.RECIPIENT_ADD', 'Add recipient')}
                  onClick={() => handleEditRecipient()}
                  loading={previewFetching}
                  data-test='add-recipient-button'
                />
              )}
            </div>
            <CoreSelect
              disabled={recipientsFetching}
              loading={recipientsFetching}
              type='default'
              options={extendedSelectOptions(filteredRecipients)}
              label={tr('FRONTEND.WITHDRAW.SKRILL.RECIPIENT', 'Recipient')}
              error={formValues.getError('destinationPaymentInstrumentId')}
              value={formValues.getValue('destinationPaymentInstrumentId').toString()}
              LeftIcon={<RecipientIcon />}
              onChange={handleRecipientChange}
              noOptionsMessage={tr('FRONTEND.WITHDRAW.SKRILL.NO_RECIPIENTS', 'No recipients created')}
            />
          </div>

          {isValidObject(selectedRecipient?.paymentOption) && (
            <SendMoneyMethodInfo
              paymentOption={selectedRecipient.paymentOption}
              currencyCode={selectedAccount?.currencyCode}
            />
          )}

          <CoreInput
            type='text'
            name='reference'
            data-test='reference'
            label={tr('FRONTEND.WITHDRAW.SKRILL.REFERENCE', 'Reference')}
            maxLength={50}
            LeftIcon={<ReferenceIcon />}
            autoComplete='off'
            {...formValues.registerInput('reference')}
          />

          <AccountingCategorySelect formValues={formValues} />

          <div className='form-actions'>
            <ErrorDisplay error={[accountsError, previewError]} />

            <MMButton
              type='button'
              size='large'
              variant='alternative'
              caption={tr('FRONTEND.BUTTONS.CONTINUE', 'Continue')}
              onClick={handlePreview}
              loading={previewFetching}
              disabled={
                accountsFetching ||
                recipientsFetching ||
                selectedRecipient?.paymentOption?.status !== PaymentOptionStatuses.AVAILABLE ||
                formValues.getFloat('amount') <= 0
              }
              data-test='button-continue'
              fullWidth
            />
          </div>
        </div>
      )}

      {currentStep === 2 && (
        <WalletTransferPreview
          formValues={formValues}
          selectedAccount={selectedAccount}
          selectedRecipient={selectedRecipient}
          onSubmit={handleAdvancedInitiate}
          onCancel={() => setCurrentStep(1)}
          onApplyPromoCode={setPromoCode}
          subFlow={WithdrawalSubFlows.SKRILL}
        />
      )}

      {currentStep === 3 && <SuccessPane response={result} onClick={() => history.push(returnToOriginUrl)} />}
    </>
  )
}

export default SkrillTransfer
